import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Users, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { confirmParticipants, acknowledgeParticipants, Participant } from '../store/conversationSlice';
import { participantReclassified } from '../store/participantActions';
import { getSpeakerColor } from '../config/participants';
import { recallWebSocketService } from '../services/recallWebSocketService';
import { Button } from './ui/button';
import { ROSTER_COPY } from '../constants/participantRoster';

type Role = 'salesperson' | 'customer';
interface RowEdit {
  role: Role;
  name: string;
}

interface ParticipantRolesModalProps {
  /** Only active in recall bot mode (a bot is selected). */
  enabled: boolean;
  /**
   * The signed-in user owns (added) this bot. Only the owner may change the
   * roster: the services refuse anyone else's edit (794364c), and the desktop
   * apps auto-open it for the creator only. A watcher never sees it here.
   */
  isBotOwner: boolean;
  /** Increment to reopen the modal on demand (the "User Roster" button). */
  openRequest?: number;
  /** The row that is the owner ("This is me"), as resolved by the page; shown pressed. */
  ownParticipantId?: string | null;
  /**
   * The owner marks a salesperson row "Me": local display state only (alert
   * labels, the speech-rate banner), never sent to the server.
   */
  onMarkOwn?: (participantId: string) => void;
}

/** The rows' starting values: the roster as this app knows it now. */
const rowEditsFrom = (participants: Participant[], keep: Record<string, RowEdit> = {}): Record<string, RowEdit> => {
  const next: Record<string, RowEdit> = { ...keep };
  participants.forEach((p, idx) => {
    if (!p?.participantId || next[p.participantId]) return;
    next[p.participantId] = {
      role: (p.role as Role) || 'customer',
      name: p.name || (p.role === 'salesperson' ? '' : `Customer ${idx + 1}`),
    };
  });
  return next;
};

/** "This is me" on one roster row: single-select, salespeople only. */
const MarkMeButton: React.FC<{ isMe: boolean; isDisabled: boolean; onClick: () => void }> = ({
  isMe,
  isDisabled,
  onClick,
}) => (
  <button
    type="button"
    aria-pressed={isMe}
    disabled={isDisabled}
    title={ROSTER_COPY.markMeHint}
    onClick={onClick}
    className={`flex-shrink-0 text-xs font-medium rounded-control border border-ink-border px-2 py-1 disabled:opacity-35 ${
      isMe ? 'bg-signature text-on-signature' : 'bg-surface text-ink-secondary hover:bg-surface-subtle'
    }`}
  >
    {ROSTER_COPY.markMe}
  </button>
);

/**
 * Real-time participant roster confirmation for bot-mode calls. As participants
 * join (tracked from recall join events), this auto-opens so the creator can
 * confirm who the salespeople are and name each participant. Multiple
 * salespeople are allowed (team-selling calls). Assignments propagate to the
 * backend (update_participants) which backfills prior turns via the saver.
 */
const ParticipantRolesModal: React.FC<ParticipantRolesModalProps> = ({
  enabled: isBotMode,
  isBotOwner,
  openRequest = 0,
  ownParticipantId = null,
  onMarkOwn,
}) => {
  const dispatch = useAppDispatch();
  const enabled = isBotMode && isBotOwner;
  const participants = useAppSelector((s) => s.conversation.participants);
  const confirmedIds = useAppSelector((s) => s.conversation.confirmedParticipantIds);

  const [isOpen, setIsOpen] = useState(false);
  const [edits, setEdits] = useState<Record<string, RowEdit>>({});

  const rosterKey = participants.map((p) => p.participantId).join(',');
  const hasUnconfirmed = useMemo(
    () => participants.some((p) => p.participantId && !confirmedIds.includes(p.participantId)),
    [participants, confirmedIds]
  );

  // Auto-open every time a new (unconfirmed) participant appears.
  useEffect(() => {
    if (enabled && hasUnconfirmed) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rosterKey]);

  // On-demand open from the "User Roster" button (works even with everyone
  // already confirmed — e.g. to rename someone mid-call).
  useEffect(() => {
    if (enabled && openRequest > 0) setIsOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest]);

  // Rows are rebuilt from the roster on every open (as both desktop apps do),
  // so a change made on another device is what the owner sees; while open, a
  // participant who joins gets a row and in-progress edits are kept.
  const seededForOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      seededForOpen.current = false;
      return;
    }
    const isFirstSeed = !seededForOpen.current;
    seededForOpen.current = true;
    setEdits((prev) => rowEditsFrom(participants, isFirstSeed ? {} : prev));
  }, [isOpen, rosterKey, participants]);

  if (!enabled || !isOpen || participants.length === 0) return null;

  const setRole = (participantId: string, role: Role) => {
    setEdits((prev) => ({ ...prev, [participantId]: { ...prev[participantId], role } }));
  };

  const setName = (participantId: string, name: string) => {
    setEdits((prev) => ({ ...prev, [participantId]: { ...prev[participantId], name } }));
  };

  const salespersonCount = Object.values(edits).filter((e) => e.role === 'salesperson').length;
  const canConfirm = salespersonCount >= 1;

  const buildAssignments = () =>
    participants
      .filter((p) => p.participantId)
      .map((p) => {
        const edit = edits[p.participantId] || { role: (p.role as Role) || 'customer', name: p.name || '' };
        const trimmedName = (edit.name || '').trim();
        return {
          participantId: p.participantId,
          role: edit.role,
          name: trimmedName || undefined,
        };
      });

  const handleConfirm = () => {
    if (!canConfirm) return;
    const assignments = buildAssignments();
    // This app's own decision (utils/participantRoleChange R1): the history
    // relabels at once; the services' echo carries their clock's confirmedAt.
    const confirmedAt = Date.now();
    dispatch(confirmParticipants(assignments, confirmedAt));
    assignments.forEach((assignment) => dispatch(participantReclassified({ ...assignment, confirmedAt, isLocal: true })));
    recallWebSocketService.updateParticipants(assignments);
    setIsOpen(false);
  };

  const handleDismiss = () => {
    // Acknowledge current roster so it doesn't immediately re-open; best-guess
    // roles stay in effect until the creator revisits.
    dispatch(acknowledgeParticipants());
    setIsOpen(false);
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-3 border-b border-hairline">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-signature" />
            <h2 className="heading-4">
              Who is on the call?
            </h2>
          </div>
          <Button variant="tertiary" onClick={handleDismiss} aria-label="Dismiss">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="px-5 py-3">
          <p className="text-xs text-ink-muted mb-3">
            Mark each person as a salesperson or a customer and add their name.
            You can mark more than one salesperson. This applies to the whole
            call and updates as new people join.
          </p>

          <div className="space-y-2 max-h-80 overflow-y-auto">
            {participants.map((p, idx) => {
              const edit = edits[p.participantId] || { role: (p.role as Role) || 'customer', name: p.name || '' };
              const color = getSpeakerColor(
                { speaker: edit.role, participantId: p.participantId },
                participants
              );
              return (
                <div
                  key={p.participantId || idx}
                  className="flex items-center gap-2 border border-hairline rounded-lg px-2 py-1.5"
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: color.bg, border: `1px solid ${color.border}` }}
                  />
                  <span className="text-xs text-ink-muted w-28 truncate" title={p.name}>
                    {p.name || `Participant ${idx + 1}`}
                  </span>
                  <select
                    value={edit.role}
                    onChange={(ev) => setRole(p.participantId, ev.target.value as Role)}
                    className="text-xs border border-ink-border rounded px-1.5 py-1 bg-surface focus:ring-2 focus:ring-signature outline-none flex-shrink-0 w-28"
                  >
                    <option value="salesperson">Salesperson</option>
                    <option value="customer">Customer</option>
                  </select>
                  <input
                    value={edit.name}
                    onChange={(ev) => setName(p.participantId, ev.target.value)}
                    placeholder={edit.role === 'salesperson' ? 'Salesperson name' : 'Customer name'}
                    className="flex-1 text-xs border border-ink-border rounded px-2 py-1 focus:ring-2 focus:ring-signature outline-none"
                  />
                  {onMarkOwn && (
                    <MarkMeButton
                      isMe={ownParticipantId === p.participantId}
                      // A customer row is never the app's user (macOS parity).
                      isDisabled={edit.role !== 'salesperson'}
                      onClick={() => onMarkOwn(p.participantId)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {!canConfirm && (
            <p className="text-xs text-error mt-2">
              Mark at least one participant as a salesperson.
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-hairline">
          <Button variant="tertiary" size="sm" onClick={handleDismiss}>
            Not now
          </Button>
          <Button variant="primary" size="sm" onClick={handleConfirm} disabled={!canConfirm}>
            Confirm roles
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantRolesModal;
